import type { JSX, ReactNode } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Link, Typography } from '@mui/material'

/**
 * Assistant-message markdown renderer.
 *
 * GFM (tables, strikethrough, task lists) via remark-gfm; raw HTML is NOT
 * rendered (react-markdown default without rehype-raw — a deliberate
 * security/robustness choice). Components are mapped onto MUI typography,
 * tuned for the 380px advisor drawer: compact margins, small type, wide
 * content (tables, code) scrolls horizontally inside its own container.
 */
const components: Components = {
  p: ({ children }) => (
    <Typography
      variant="body2"
      sx={{ my: 0.5, '&:first-of-type': { mt: 0 }, '&:last-of-type': { mb: 0 } }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => <Heading>{children}</Heading>,
  h2: ({ children }) => <Heading>{children}</Heading>,
  h3: ({ children }) => <Heading>{children}</Heading>,
  h4: ({ children }) => <Heading>{children}</Heading>,
  h5: ({ children }) => <Heading>{children}</Heading>,
  h6: ({ children }) => <Heading>{children}</Heading>,
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 0.5, pl: 2.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ typography: 'body2', my: 0.25 }}>
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        m: 0,
        my: 0.5,
        p: 1,
        bgcolor: 'action.hover',
        borderRadius: 1,
        overflowX: 'auto',
        fontSize: '0.75rem',
        '& code': { bgcolor: 'transparent', px: 0 },
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8em',
        bgcolor: 'action.hover',
        px: 0.5,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 0.5 }}>
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          '& th, & td': {
            border: 1,
            borderColor: 'divider',
            px: 0.75,
            py: 0.25,
            typography: 'caption',
            textAlign: 'left',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{ m: 0, my: 0.5, pl: 1, borderLeft: 2, borderColor: 'divider', color: 'text.secondary' }}
    >
      {children}
    </Box>
  ),
  hr: () => <Box component="hr" sx={{ border: 0, borderTop: 1, borderColor: 'divider', my: 1 }} />,
}

function Heading({ children }: { children?: ReactNode }): JSX.Element {
  // All heading levels collapse to one compact treatment — a 380px drawer has
  // no room for a real heading scale, and the prompt discourages headings anyway.
  return (
    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.25, fontWeight: 700 }}>
      {children}
    </Typography>
  )
}

export function MarkdownMessage({ text }: { text: string }): JSX.Element {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {text}
    </ReactMarkdown>
  )
}
